import {idFromGlobalId} from '../../../forecast-app/shared/util/GlobalIdUtil';
import {
	countEligibleGrantedCompanies,
	countIndividualUserGrants,
	distinctGrantedCompanyIds,
	resolveRolloutPercent,
} from './featureFlagRows';
import {FeatureFlagTeamId, getTeam, resolveTeamId, UNMAPPED_TEAM_COLOUR} from './featureFlagTeams';

/** All-users grant shape the detail screen needs — a structural subset of the Relay result. */
export interface DetailCompanyGrant {
	readonly companyId: string | null;
	/** The company's name, as recorded on the grant. */
	readonly name: string | null;
	readonly timestamp: string | null;
}

/** Individual grant shape the detail screen needs — a structural subset of the Relay result. */
export interface DetailPersonGrant {
	readonly personId: string | null;
	readonly companyId: string | null;
	readonly companyName: string | null;
	readonly name: string | null;
	readonly email: string | null;
	readonly timestamp: string | null;
}

export interface DetailFeatureSource {
	readonly key: string | null;
	readonly name: string | null;
	readonly team: string | null;
	readonly rolledOut: boolean | null;
	readonly companies: ReadonlyArray<DetailCompanyGrant | null> | null;
	readonly persons: ReadonlyArray<DetailPersonGrant | null> | null;
}

/** One company's access to the flag — either everyone in it, or the named users inside it. */
export interface FeatureFlagAssignmentRow {
	/** Encoded `CompanyType` id. The row key, and the identity a future rollout modal will act on. */
	companyId: string;
	companyName: string;
	/** True when the company holds an all-users grant. Then `users` is empty and `userCountLabel` is ''. */
	allUsers: boolean;
	/** `1 user` / `N users`. Empty when `allUsers`. */
	userCountLabel: string;
	/** `Name (email)` per granted person, in the order the backend returned them. */
	users: string[];
	/**
	 * Encoded `Person` global id per entry in `users`, same order and length. Optional so existing
	 * fixtures that don't exercise the rollout modal's edit path need no change. Null for an entry the
	 * backend recorded no id for (should not happen in practice — every person grant carries one).
	 */
	userPersonIds?: Array<string | null>;
	/** YYYY-MM-DD, or null when the backend recorded no timestamp. */
	added: string | null;
}

export interface FeatureFlagDetailData {
	key: string;
	name: string;
	/** null only when the backend sends a team `featureFlagTeams` does not know yet. */
	teamId: FeatureFlagTeamId | null;
	/** The mapped team's label, or the backend's raw team string when unmapped. */
	teamLabel: string;
	teamColour: string;
	/** Every all-users grant — the same number the flag's list row shows. */
	companyCount: number;
	/** Individual grants outside all-users companies — the same number the flag's list row shows. */
	userCount: number;
	/** Granted companies inside the eligible pool: the numerator of the percentage. */
	eligibleGranted: number;
	/** Size of the eligible pool: the denominator. */
	eligibleTotal: number;
	/** Granted companies outside the eligible pool, so not counted by the percentage. */
	ineligibleGrantCount: number;
	/** 0-100. Bounded by construction — both sides of the ratio come from the eligible pool. */
	rolloutPercent: number;
	rolloutPercentLabel: string;
	rows: FeatureFlagAssignmentRow[];
}

const isPresent = <T>(value: T | null | undefined): value is T => value != null;

/** The date part of a backend timestamp, matching how the flag list renders its dates. */
const toDate = (timestamp: string | null): string | null => (timestamp ? timestamp.substring(0, 10) : null);

/**
 * The latest of a set of timestamps, as YYYY-MM-DD. Sorted as strings, which orders ISO dates
 * correctly without pulling in a date library.
 */
const latestDate = (timestamps: ReadonlyArray<string | null>): string | null =>
	timestamps.map(toDate).filter(isPresent).sort().pop() ?? null;

/**
 * A person's name with the backend's stringified blanks removed.
 *
 * A person's name reaches this screen as their first and last name joined with a space, and a half
 * the database never recorded is stringified on the way — so someone with no surname arrives as
 * `"Ana null"` and an invitee who has not filled anything in as `"null null"`. Dropping those
 * segments is what keeps the literal word "null" out of a name on the page. A real name segment of
 * exactly `null` in lower case would be dropped too, which no name is.
 */
const usableName = (name: string | null): string =>
	(name ?? '')
		.split(/\s+/)
		.filter(segment => segment !== '' && segment !== 'null')
		.join(' ');

/** `Name (email)`, degrading to whichever of the two the backend has. */
const personLabel = (person: DetailPersonGrant): string => {
	const name = usableName(person.name);
	const email = person.email ?? '';
	if (name && email) {
		return `${name} (${email})`;
	}
	return name || email;
};

/**
 * Something identifying to call a company the backend recorded no name for.
 *
 * The numeric company id is what an admin can actually act on — look it up, search for it — and it
 * keeps every row's edit control uniquely labelled, where an empty name would leave several rows
 * announcing the same thing and sort them all to the top of the table.
 */
const companyDisplayName = (name: string | null | undefined, encodedCompanyId: string): string => {
	if (name) {
		return name;
	}
	const numericId = idFromGlobalId(encodedCompanyId);
	return `Company ${Number.isNaN(numericId) ? encodedCompanyId : numericId}`;
};

/**
 * Grants bucketed by the company they belong to, keeping the order the backend returned them in.
 *
 * A grant with no company id is dropped: there is no company for it to be listed under. `include`
 * lets a caller skip companies it already has a row for.
 */
function groupByCompanyId<T extends {readonly companyId: string | null}>(
	grants: ReadonlyArray<T>,
	include?: (companyId: string) => boolean
): Map<string, T[]> {
	const grouped = new Map<string, T[]>();
	grants.forEach(grant => {
		if (grant.companyId == null || (include && !include(grant.companyId))) {
			return;
		}
		const existing = grouped.get(grant.companyId);
		if (existing) {
			existing.push(grant);
		} else {
			grouped.set(grant.companyId, [grant]);
		}
	});
	return grouped;
}

/**
 * One row per company, sorted by company name.
 *
 * A company-level grant supersedes any named users in the same company: everyone there has the flag,
 * so listing individuals as well would overstate what is configured. This is the same precedence the
 * flag list's `userCount` and the check-company modal already apply.
 */
function buildAssignmentRows(
	companyGrants: ReadonlyArray<DetailCompanyGrant>,
	personGrants: ReadonlyArray<DetailPersonGrant>,
	allUsersCompanyIds: Set<string>
): FeatureFlagAssignmentRow[] {
	const rowsByCompanyId = new Map<string, FeatureFlagAssignmentRow>();

	const grantsByCompanyId = groupByCompanyId(companyGrants);
	grantsByCompanyId.forEach((grants, companyId) => {
		rowsByCompanyId.set(companyId, {
			companyId,
			companyName: companyDisplayName(grants.find(grant => grant.name)?.name, companyId),
			allUsers: true,
			userCountLabel: '',
			users: [],
			userPersonIds: [],
			// The company's most recent grant, the same rule the named-users rows below use, so the
			// column answers "when did this company last gain access" whichever kind of row it is.
			added: latestDate(grants.map(grant => grant.timestamp)),
		});
	});

	const personsByCompanyId = groupByCompanyId(personGrants, companyId => !allUsersCompanyIds.has(companyId));
	personsByCompanyId.forEach((persons, companyId) => {
		rowsByCompanyId.set(companyId, {
			companyId,
			companyName: companyDisplayName(persons.find(person => person.companyName)?.companyName, companyId),
			allUsers: false,
			userCountLabel: persons.length === 1 ? '1 user' : `${persons.length} users`,
			users: persons.map(personLabel),
			userPersonIds: persons.map(person => person.personId),
			added: latestDate(persons.map(person => person.timestamp)),
		});
	});

	return Array.from(rowsByCompanyId.values()).sort((a, b) => a.companyName.localeCompare(b.companyName));
}

/**
 * The flag a detail URL refers to, or null when it has no detail page to show.
 *
 * Null covers both a key no flag has and a rolled-out flag: rolled-out flags are configured in code,
 * so there is nothing on a detail page to say about them. Callers redirect back to the list on null.
 */
export function findDetailFeature(
	features: ReadonlyArray<DetailFeatureSource | null> | null | undefined,
	flagKey: string
): DetailFeatureSource | null {
	const feature = (features ?? []).filter(isPresent).find(candidate => candidate.key === flagKey);
	if (!feature || feature.rolledOut === true) {
		return null;
	}
	return feature;
}

/**
 * Everything a flag's detail page shows, derived from one feature and the eligible company pool.
 *
 * `eligibleCompanyIds` is the same set the list page measures its percentages against, and both
 * counts the two screens share are derived by calling the same helpers the list row calls, so the two
 * agree by construction rather than by coincidence.
 */
export function buildFeatureFlagDetail(feature: DetailFeatureSource, eligibleCompanyIds: Set<string>): FeatureFlagDetailData {
	const companyGrants = (feature.companies ?? []).filter(isPresent);
	const personGrants = (feature.persons ?? []).filter(isPresent);
	const allUsersCompanyIds = distinctGrantedCompanyIds(companyGrants);

	const eligibleGranted = countEligibleGrantedCompanies(allUsersCompanyIds, eligibleCompanyIds);
	const {percent, label} = resolveRolloutPercent(eligibleGranted, eligibleCompanyIds.size);

	const teamId = resolveTeamId(feature.team);

	return {
		key: feature.key ?? '',
		name: feature.name ?? '',
		teamId,
		teamLabel: teamId ? getTeam(teamId).label : feature.team ?? '',
		teamColour: teamId ? getTeam(teamId).colour : UNMAPPED_TEAM_COLOUR,
		companyCount: companyGrants.length,
		userCount: countIndividualUserGrants(personGrants, allUsersCompanyIds),
		eligibleGranted,
		eligibleTotal: eligibleCompanyIds.size,
		// Companies that hold the flag but sit outside the eligible pool, so the percentage does not
		// measure them. Counted off the distinct companies rather than the raw grant list, because the
		// number is spelled out to the reader as companies — a company that somehow holds two grants is
		// still one company outside the pool, and would otherwise be reported as one that is inside it.
		ineligibleGrantCount: allUsersCompanyIds.size - eligibleGranted,
		rolloutPercent: percent,
		rolloutPercentLabel: label,
		rows: buildAssignmentRows(companyGrants, personGrants, allUsersCompanyIds),
	};
}
